import { Hidden } from '@ahmdigital/ui';
import { upperFirst } from 'lodash/fp';
import { useFormikContext } from 'formik';
import Icon, { IconList } from '@ahmdigital/icons-web';
import React, { useEffect, useRef, useState } from 'react';

import {
  ErrorText,
  FieldWrapper,
  IconWrapper,
  InnerInputWrapper,
  InputWrapper,
  Label,
  ModalWrapper,
  PlaceholderText,
  StyledButton,
  ToggleWrapper,
  Wrapper,
} from './styles';
import analytics from '../../../../analytics';
import getPanelIndexForError from '../../utils/get-panel-index-for-error';
import useEmbeddedWidgetContext from '../../hooks/use-embedded-widget-context';

export type ModalComponentProps = {
  autoCompleteRef?: React.RefObject<HTMLDivElement>;
  setIsModalOpen?: (value: boolean) => void;
  setFieldTouched?: (field: string, value?: boolean, shouldValidate?: boolean) => void;
  modalContentRef?: React.RefObject<HTMLDivElement>;
  setIsSuggestionsOpen?: (value: boolean) => void;
};

export type StepInputProps = {
  displayValue: {
    getLabel: () => string;
    isActive: boolean;
  };
  handleCloseWidgetOnTab: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  hasButton?: boolean;
  icon: string;
  index: number;
  isFirst?: boolean;
  isLast?: boolean;
  isModalOpen?: boolean;
  modalComponent?: React.FC<ModalComponentProps>;
  name: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  placeholder?: string;
  setIsModalOpen: (value: boolean) => void;
  setIsWidgetActive: (value: boolean) => void;
  inputComponent?: React.FC<ModalComponentProps>;
};

const StepInput = <T,>({
  displayValue,
  handleCloseWidgetOnTab,
  hasButton = false,
  icon,
  index,
  inputComponent: InputComponent,
  isFirst,
  isLast,
  isModalOpen,
  modalComponent: ModalComponent,
  name,
  onMouseEnter,
  onMouseLeave,
  placeholder,
  setIsModalOpen,
}: StepInputProps) => {
  const sharedInputProps = {
    isLast,
    label: `${upperFirst(name)}*`,
    name,
    placeholder,
  };
  const sectionRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const autoCompleteRef = useRef<HTMLDivElement>(null);

  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const { handleSubmit, errors, isSubmitting, setFieldTouched, submitCount } = useFormikContext<T>();
  const errorText = errors[name as keyof T];
  const showErrorText = errorText && isModalOpen && hasError;

  const { modalFields } = useEmbeddedWidgetContext<T>();
  const firstErrorPanelIndex = getPanelIndexForError({ errors, modalFields });
  useEffect(() => {
    if (firstErrorPanelIndex === null) {
      setHasError(false);
      return;
    }
    const isCurrentPanel = firstErrorPanelIndex === index && !!isModalOpen;
    if (submitCount > 0) {
      setHasError(isCurrentPanel);
      if (isCurrentPanel) {
        sectionRef.current?.focus();
      }
    }
  }, [firstErrorPanelIndex, isSubmitting, isModalOpen]);

  const handleFocus = () => {
    setIsModalOpen(true);
  };
  const handleClick = () => {
    autoCompleteRef.current?.focus();
    analytics.publish('action', {
      action: 'Clicked',
      component: 'Element',
      label: `Get a quote ${name}`,
    });
  };
  return (
    <Wrapper isLast={isLast} onClick={handleClick}>
      <FieldWrapper
        hasError={hasError}
        isFirst={isFirst}
        isLast={isLast}
        isOpen={isModalOpen || isSuggestionsOpen}
        isButtonHovered={isButtonHovered}
        onFocus={handleFocus}
        onBlur={() => {
          setIsSuggestionsOpen(false);
        }}
        tabIndex={0}
        hasButton={hasButton}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={sectionRef}
      >
        <InputWrapper
          hasError={hasError}
          isOpen={isModalOpen || isSuggestionsOpen}
          isFirst={isFirst}
          isLast={isLast}
          hasButton={hasButton}
        >
          <InnerInputWrapper>
            {InputComponent ? (
              <InputComponent
                setIsModalOpen={setIsModalOpen}
                modalContentRef={modalContentRef}
                setIsSuggestionsOpen={setIsSuggestionsOpen}
                autoCompleteRef={autoCompleteRef}
                {...sharedInputProps}
              />
            ) : (
              <ToggleWrapper isLast={isLast}>
                <Label>{upperFirst(name)}*</Label>
                <PlaceholderText hasValue={displayValue.isActive}>{displayValue.getLabel()}</PlaceholderText>
              </ToggleWrapper>
            )}
            <IconWrapper>
              <Icon name={icon as IconList} size="medium" />
            </IconWrapper>
          </InnerInputWrapper>
          <Hidden xs sm md lg>
            {showErrorText && <ErrorText>{errorText}</ErrorText>}
          </Hidden>
        </InputWrapper>
      </FieldWrapper>

      {isSuggestionsOpen && !isModalOpen && (
        <ModalWrapper isFirst={isFirst} isLast={isLast} hasError={hasError} isOpen={isSuggestionsOpen}>
          <div ref={modalContentRef} />
        </ModalWrapper>
      )}

      {isModalOpen && !isSuggestionsOpen && ModalComponent && (
        <ModalWrapper isFirst={isFirst} isLast={isLast} hasError={hasError} isOpen={isModalOpen}>
          {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ children: false | Element | undefined; df: true; xl: true; }' is not assignable to type 'IntrinsicAttributes & Pick<HiddenProps, "children"> & Partial<Pick<HiddenProps, "xs" | "sm" | "md" | "lg" | "df" | "isBlock">> & Partial<...>'. */}
          <Hidden df xl>
            {showErrorText && <ErrorText>{errorText}</ErrorText>}
          </Hidden>
          <ModalComponent
            autoCompleteRef={autoCompleteRef}
            setIsModalOpen={setIsModalOpen}
            setFieldTouched={setFieldTouched}
          />
        </ModalWrapper>
      )}
      {hasButton && (
        <Hidden xs sm md>
          <StyledButton
            icon={{
              name: 'arrow-right-long',
              position: 'right',
              size: 'large',
            }}
            // @ts-expect-error [🤖 PLEASE FIX 🤖]: No overload matches this call.
            onClick={handleSubmit}
            variant="flair"
            onMouseEnter={() => {
              setIsButtonHovered(true);
              onMouseEnter();
            }}
            onMouseLeave={() => setIsButtonHovered(false)}
            isLoading={isSubmitting}
            onKeyDown={handleCloseWidgetOnTab}
          >
            <Hidden lg>Get quote</Hidden>
          </StyledButton>
        </Hidden>
      )}
    </Wrapper>
  );
};

export default StepInput;
